/* eslint-disable no-useless-escape */
import {
  Box,
  Dialog,
  DialogTitle,
  Grid,
  IconButton,
  Tabs,
  Tab,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Message } from '../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { normalizeQuillHtml } from 'views/email-templates/components/notificationSelected';
import { emailTemplate } from 'views/email-templates/assets/emailTemplate';
import PostalMime, { Email } from "postal-mime";
import RawMessageModal from './raw-message-modal';

interface MessageModalProps {
  open: boolean;
  handleClose: () => void;
  message: Message;
  t: TFunction;
}

// Already-sent emails carry their own fixed 500px template, so the reception HTML
// preview is constrained at render time instead of on the (immutable) source.
const RECEPTION_PREVIEW_FIX = `
  html, body { max-width: 100% !important; overflow-x: hidden !important; }
  body { margin: 0 !important; }
  div { width: 100% !important; max-width: 500px !important; margin-left: auto !important; margin-right: auto !important; }
  img { max-width: 100% !important; height: auto; }
`;

const useStyles = makeStyles((theme: any) =>
  createStyles({
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0,
      padding: theme.spacing(0, 1, 0, 0),
      [theme.breakpoints.up('sm')]: { padding: theme.spacing(1) },
    },
    // Stacked layout (below md): tabs switch between the message preview and receptions.
    tabsBar: {
      flexShrink: 0,
      '& .MuiTab-root': { textTransform: 'none' },
    },
    bodyStacked: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      width: '100%',
    },
    messagePanel: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      padding: theme.spacing(1, 2, 1),
      paddingBottom: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom))`,
    },
    receptionsPanel: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      padding: theme.spacing(1, 2, 0),
      paddingBottom: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom))`,
    },
    emptyState: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(4, 2),
      textAlign: 'center',
    },
    emptyText: {
      color: theme.palette.text.light,
    },
    // Desktop two-column body.
    containerGrid: {
      height: 'calc(100% - 80px)',
      flexWrap: 'nowrap',
      gap: theme.spacing(2),
      padding: theme.spacing(1.25),
    },
    column: {
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    columnHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      minHeight: theme.spacing(6),
      flexShrink: 0,
    },
    receptionsActionsRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      flexShrink: 0,
      minHeight: theme.spacing(6),
    },
    optionsButton: {
      flexShrink: 0,
    },
    preview: {
      width: '100%',
      flex: 1,
      minHeight: 0,
      border: 'unset',
    },
    receptions: {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    receptionSelector: {
      flexShrink: 0,
      borderBottom: `1px solid ${theme.palette.neutraldivider}`,
      marginBottom: theme.spacing(2),
      '& .MuiTab-root': { textTransform: 'none', minWidth: theme.spacing(6) },
    },
    htmlFrame: {
      flex: 1,
      minHeight: 0,
      border: `1px solid ${theme.palette.border.line}`,
      borderRadius: 4,
      overflow: 'hidden',
      background: theme.palette.background.paper,
    },
    htmlFrameIframe: {
      display: 'block',
      width: '100%',
      height: '100%',
      border: 'unset',
    },
  })
);

const MessageModal: FC<MessageModalProps> = ({ open, handleClose, message, t }) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isStacked = useMediaQuery(theme.breakpoints.down('lg'));
  const { t: tOrders } = useTranslation('orders');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const receptionIframeRef = useRef<HTMLIFrameElement>(null);
  const writtenPreviews = useRef(new WeakSet<HTMLIFrameElement>());
  const [receptions, setReceptions] = useState<Email[]>([]);
  const [selectedTabIndex, setSelectedIndex] = useState<number>(0);
  const [stackedTab, setStackedTab] = useState<number>(0);
  const [rawModalOpen, setRawModalOpen] = useState(false);

  const rawMessage = message.receptions[selectedTabIndex] || '';
  const hasRawMessage = Boolean(rawMessage);

  // Write on load: tab/breakpoint switches remount the iframe, so an effect would leave it blank.
  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (!iframe || !doc || writtenPreviews.current.has(iframe)) return;
    const finalHtml = emailTemplate.replace(
      'REPLACE_THIS_WITH_CONTENT',
      normalizeQuillHtml(message.content)
    );
    doc.open();
    doc.write(finalHtml);
    doc.close();
    writtenPreviews.current.add(iframe);
  };

  const handleReceptionIframeLoad = () => {
    const doc = receptionIframeRef.current?.contentDocument;
    if (!doc || !doc.head || doc.getElementById('reception-preview-fix')) return;
    const style = doc.createElement('style');
    style.id = 'reception-preview-fix';
    style.textContent = RECEPTION_PREVIEW_FIX;
    doc.head.appendChild(style);
  };

  useEffect(() => {
    (async () => {
      const receptionsArray: Email[] = [];
      // eslint-disable-next-line no-plusplus
      for (let index = 0; index < message.receptions.length; index++) {
        const email = await PostalMime.parse(message.receptions[index], {});
        email.html = email.html?.replace(/cid:logo.*?\"/g, `/assets/championship.png"`);
        email.html = email.html?.replace(/cid:city.*?\"/g, `/assets/city.jpg"`);
        receptionsArray.push(email);
      }
      setReceptions(receptionsArray);
    })();
  }, [message.receptions]);

  const renderRawMessageButton = () => {
    if (!hasRawMessage) return null;
    return (
      <IconButton
        className={classes.optionsButton}
        aria-label={t('messages:options')}
        onClick={() => setRawModalOpen(true)}
        size="large"
      >
        <FontAwesomeIcon icon="ellipsis-v" />
      </IconButton>
    );
  };

  const renderReceptionSelector = () => {
    // A single reception needs no selector — avoid a lone "#1".
    if (receptions.length <= 1) return null;
    return (
      <Box className={classes.receptionSelector}>
        <Tabs
          value={selectedTabIndex}
          onChange={(_, value: number) => setSelectedIndex(value)}
          scrollButtons="auto"
          variant="scrollable"
        >
          {receptions.map((_, index) => {
            // eslint-disable-next-line react/no-array-index-key
            return <Tab key={index} label={<Typography color="primary">{`#${index + 1}`}</Typography>} value={index} />;
          })}
        </Tabs>
      </Box>
    );
  };

  const renderReceptionHtml = () => {
    if (selectedTabIndex >= receptions.length) return null;
    return (
      <Box className={classes.htmlFrame}>
        <iframe
          ref={receptionIframeRef}
          title="reception-html"
          srcDoc={receptions[selectedTabIndex].html}
          onLoad={handleReceptionIframeLoad}
          className={classes.htmlFrameIframe}
        />
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        fullScreen={fullScreen}
        maxWidth="xl"
        PaperProps={{
          style: { height: "100%" }
        }}
      >
        <Box className={classes.header}>
          <DialogTitle><Typography color="primary" variant="h5" component="h2">{t('messages:message')}</Typography></DialogTitle>

          <IconButton aria-label={t('common:close')} onClick={() => handleClose()} size="large">
            <FontAwesomeIcon icon="times" />
          </IconButton>
        </Box>

        {isStacked && (
        <Box className={classes.tabsBar} borderBottom={1} borderColor="divider">
          <Tabs
            value={stackedTab}
            onChange={(_, value: number) => setStackedTab(value)}
            variant="fullWidth"
          >
            <Tab label={<Typography color="primary">{t('messages:preview')}</Typography>} value={0} />
            <Tab label={<Typography color="primary">{tOrders('receptionsTitle')}</Typography>} value={1} />
          </Tabs>
        </Box>
        )}

        {isStacked ? (
          <Box className={classes.bodyStacked}>
            {/* Kept mounted (only hidden) so the preview iframe is never destroyed on tab switch. */}
            <Box className={classes.messagePanel} style={{ display: stackedTab === 0 ? 'flex' : 'none' }}>
              <iframe
                title="preview"
                ref={iframeRef}
                onLoad={handleIframeLoad}
                srcDoc="<!DOCTYPE html><html><head></head><body></body></html>"
                className={classes.preview}
              />
            </Box>
            {stackedTab === 1 && (receptions.length > 0 ? (
              <Box className={classes.receptionsPanel}>
                {hasRawMessage && (
                <Box className={classes.receptionsActionsRow}>
                  {renderRawMessageButton()}
                </Box>
                )}
                {renderReceptionSelector()}
                {renderReceptionHtml()}
              </Box>
            ) : (
              <Box className={classes.emptyState}>
                <Typography variant="body2" className={classes.emptyText}>{tOrders('noReceptionInfo')}</Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Grid container direction="row" className={classes.containerGrid}>
            <Grid item xs={12} md={6} className={classes.column}>
              <Box className={classes.columnHeader}>
                <Typography color="primary" variant="h5">{t('messages:preview')}</Typography>
              </Box>
              <iframe
                title="preview"
                ref={iframeRef}
                onLoad={handleIframeLoad}
                srcDoc="<!DOCTYPE html><html><head></head><body></body></html>"
                className={classes.preview}
              />
            </Grid>
            {receptions.length > 0 ? (
              <Grid item xs={12} md={6} className={classes.column}>
                <Box className={classes.columnHeader}>
                  <Typography color="primary" variant="h5">{tOrders('receptionsTitle')}</Typography>
                  {renderRawMessageButton()}
                </Box>
                {renderReceptionSelector()}
                <Box className={classes.receptions}>
                  {renderReceptionHtml()}
                </Box>
              </Grid>
            ) : (
              <Grid item xs={12} md={6} className={classes.column}>
                <Box className={classes.columnHeader}>
                  <Typography color="primary" variant="h5">{tOrders('receptionsTitle')}</Typography>
                </Box>
                <Typography>{tOrders('noReceptionInfo')}</Typography>
              </Grid>
            )}
          </Grid>
        )}
      </Dialog>
      <RawMessageModal
        open={rawModalOpen}
        handleClose={() => setRawModalOpen(false)}
        rawMessage={rawMessage}
        title={tOrders('rawMessageTitle')}
        t={t}
      />
    </>
  );
};

export default MessageModal;
